import { countPlayoutOverrun, isNoteTraceEnabled, traceNote } from "@/lib/live/note-trace";
import { Lane, LaneEvent, PlayedNote } from "./lane";
import { LocalTransport } from "./local-transport";

export class Scheduler {
  private scheduleIndex: number = 0;
  private unsubscribers: (() => void)[] = [];

  // ── Scheduled notes (from playback / onSchedule) ──
  private scheduledNotes: Map<number, PlayedNote> = new Map();
  private scheduledSustainedNotes: Set<number> = new Set();
  private scheduledSustainActive: boolean = false;

  // ── Live notes (from PianoControler / lane.on events) ──
  private liveNotes: Map<number, PlayedNote> = new Map();
  private liveSustainedNotes: Set<number> = new Set();
  private liveSustainActive: boolean = false;

  constructor(private transport: LocalTransport, private lane: Lane) {
    const unregisterTransport = transport.register((fromTick, scheduleUntilTick) => {
      this.onSchedule(fromTick, scheduleUntilTick);
    });
    this.unsubscribers.push(unregisterTransport);

    this.unsubscribers.push(
      transport.on((e) => {
        if (e.type === 'seek') {
          this.scheduleIndex = this.lane.getIndexFromTick(e.tick);
          this.stopScheduledNotes();
          // La pédale du morceau est un état, pas un événement qu'on vient de
          // lire : tomber au milieu d'une pédale tenue, c'est arriver avec
          // l'étouffoir déjà levé. Sans cette ligne elle resterait celle de
          // l'endroit d'où l'on vient — enfoncée pour toujours en remontant
          // avant le morceau, levée sur un passage entièrement pédalé.
          //
          // Celle du joueur, elle, ne se recalcule pas : son pied ne bouge pas
          // parce qu'on a tourné la molette.
          this.scheduledSustainActive = this.lane.isSustainDownAt(e.tick);
        } else if (e.type === 'play') {
          this.catchUpSoundingNotes(e.tick);
        }
      })
    );

    this.unsubscribers.push(
      this.lane.on((e) => {
        if (e.type === 'mute') {
          // Mute only stops scheduled (playback) notes, live notes are never affected
          this.stopScheduledNotes();
          return;
        }

        if (e.type === 'clear') {
          // La lane a tout effacé : notre indice pointait dans un tableau qui
          // n'existe plus.
          this.scheduleIndex = 0;

          return;
        }

        if (e.type === 'unmute' || e.type === 'hide' || e.type === 'show' || e.type === 'clusterChange') return;
        // Live events (from PianoControler) are emitted with a tick in the present,
        // which falls behind the onSchedule lookahead window. Handle them immediately
        // and advance scheduleIndex so onSchedule won't re-trigger them.
        this.handleLiveEvent(e);
      })
    );
  }

  private onSchedule(_fromTick: number, toTick: number) {
    if (this.lane.isMuted()) return;

    if (this.transport.isDetached) {
      // Only release scheduled (playback) notes, never kill live notes
      this.stopScheduledNotes();
      return;
    }

    const index = this.scheduleIndex ?? 0;

    const eventsToSchedule = this.lane.getEventsFromIndex(index, toTick);

    for (const e of eventsToSchedule) {
      this.handleScheduledEvent(e);
    }
  }

  // ── Scheduled event handling (playback) ──

  private handleScheduledEvent(e: LaneEvent) {
    if (!('tick' in e)) return;
    const tick = e.tick!;

    // La lecture programmée, elle, convertit : `onSchedule` travaille en avance
    // sur la tête de lecture, et chaque note doit sonner à son heure.
    const eventTime = this.transport.getTimeFromTick(tick);

    if (e.type === 'noteOn') {
      this.scheduledNotes.set(e.note.toMidi(), e);
      this.lane.synth.noteOn(e.note.toMidi(), e.velocity, eventTime);
    } else if (e.type === 'noteOff') {
      if (this.scheduledSustainActive) {
        this.scheduledSustainedNotes.add(e.note.toMidi());
      } else {
        this.lane.synth.noteOff(e.note.toMidi(), eventTime);
      }
      this.scheduledNotes.delete(e.note.toMidi());
    } else if (e.type === 'sustainOn') {
      this.scheduledSustainActive = true;
    } else if (e.type === 'sustainOff') {
      this.scheduledSustainActive = false;
      // À l'heure de la pédale, et non maintenant : `onSchedule` travaille en
      // avance sur la tête de lecture, et un relâchement daté « tout de suite »
      // couperait chaque note tenue de cette avance.
      this.releaseScheduledSustainedNotes(eventTime);
    }

    this.scheduleIndex++;
  }

  // ── Live event handling (PianoControler, never muted) ──

  private handleLiveEvent(e: LaneEvent) {
    if (!('tick' in e)) return;
    const tick = e.tick!;

    // Une lane datée sur le transport partagé — un partenaire de séance — ne
    // sonne tout de suite que si notre vue est au live. Sinon on la laisse
    // passer sans rien jouer : elle vient d'être écrite dans la lane à sa
    // place, et `onSchedule` la prendra comme n'importe quelle note quand la
    // tête de lecture arrivera dessus. D'où le retour **avant** l'avance de
    // `scheduleIndex`, qui la ferait sauter.
    if (this.lane.hasSharedTimebase && !this.transport.isFollowingShared) {
      return;
    }

    // Un geste live sonne **à son tick s'il est encore devant, maintenant
    // sinon**.
    //
    // Le tick est une vérité : il dit quand le geste a été joué, chez moi
    // comme chez un partenaire. Ce qui le met devant ou derrière, c'est le
    // transport : en séance, il fait courir la tête de lecture du délai de
    // restitution derrière la position de séance, et la capture se fait au
    // présent vrai. Une note — la mienne ou celle d'un pair — arrive donc avec
    // un tick que la tête n'a pas encore atteint, et se programme exactement à
    // son heure. C'est ce qui fait tomber tout le monde sur la même grille, et
    // ce qui tient la gigue du réseau hors du rythme entendu.
    //
    // La branche « au plus tôt » reste, mais elle n'est plus un régime :
    //
    //   - **solo** (délai nul) : le tick capté *est* l'instant présent,
    //     `at == now`, et jouer au plus tôt est exact ;
    //   - **alarme** : un délai est en vigueur et une note de partenaire
    //     arrive quand même en retard — le tampon est plus court que le trajet
    //     et sa gigue. Jouer en retard vaut mieux que ne pas jouer, mais elle a
    //     sonné hors de la grille et ça doit se compter.
    const at = this.transport.getTimeFromTick(tick);
    const now = this.transport.audioTimeIn(0);

    // Un geste du joueur lui-même sonne à l'instant où il le joue quand sa vue
    // est figée : la tête ne bouge pas, et programmer la note à son tick la
    // ferait attendre une heure qui n'arrive jamais. Une vue qui défile, même
    // déplacée à la molette, capte à sa tête plus le délai (voir
    // `LocalTransport.captureTick`) : la note tombe sur sa grille comme quand
    // la vue suit le direct. Un partenaire, lui, ne sonne pas du tout quand la
    // vue a décroché, c'est la garde ci-dessus.
    const ownGestureOffGrid = !this.lane.hasSharedTimebase && this.transport.isDetached;
    const eventTime = at > now && !ownGestureOffGrid ? at : undefined;

    // Compté toujours, tracé seulement à la demande : le dépassement doit se
    // voir sans armer le journal, et sans un `console.warn` par note au moment
    // le plus chargé.
    if (eventTime === undefined && this.lane.hasSharedTimebase && this.transport.getPlayoutDelay() > 0) {
      countPlayoutOverrun();
    }

    if (isNoteTraceEnabled() && 'type' in e) {
      this.trace(e, tick, at, now, eventTime);
    }

    if (e.type === 'noteOn') {
      this.liveNotes.set(e.note.toMidi(), e);
      this.lane.synth.noteOn(e.note.toMidi(), e.velocity, eventTime);
    } else if (e.type === 'noteOff') {
      if (this.liveSustainActive) {
        this.liveSustainedNotes.add(e.note.toMidi());
      } else {
        this.lane.synth.noteOff(e.note.toMidi(), eventTime);
      }
      this.liveNotes.delete(e.note.toMidi());
    } else if (e.type === 'sustainOn') {
      this.liveSustainActive = true;
    } else if (e.type === 'sustainOff') {
      this.liveSustainActive = false;
      // À l'instant de la pédale, pas à celui où on l'apprend : sous délai de
      // restitution, le `sustainOff` d'un partenaire porte un tick encore
      // *devant* notre tête de lecture, et relâcher tout de suite couperait ses
      // notes tenues jusqu'à un délai trop tôt.
      this.releaseLiveSustainedNotes(eventTime);
    }

    this.scheduleIndex++;
  }

  /**
   * Relève ce qu'un geste live est devenu, sur la grille partagée.
   *
   * Tout est ramené en millisecondes de séance, et pas en temps audio : c'est
   * la seule échelle qui veuille dire la même chose sur les deux machines,
   * puisqu'elle découle de l'origine que le service de temps impose à tout le
   * monde. On la reconstruit à partir de la position courante et de l'écart
   * *audio* entre les instants — l'écart, lui, est juste sur n'importe quelle
   * échelle.
   */
  private trace(
    e: LaneEvent & { type: string },
    tick: number,
    at: number,
    now: number,
    eventTime: number | undefined,
  ) {
    if (e.type !== 'noteOn' && e.type !== 'noteOff' && e.type !== 'sustainOn' && e.type !== 'sustainOff') {
      return;
    }

    const posMs = this.transport.transport.elapsedTime() * 1000;
    const played = eventTime ?? now;
    const note = 'note' in e && e.note !== undefined ? e.note.toMidi() : -1;

    traceNote({
      role: "play",
      lane: this.lane.hasSharedTimebase ? (this.laneLabel() ?? "pair") : "moi",
      kind: e.type === 'noteOn' ? "noteOn" : e.type === 'noteOff' ? "noteOff" : "sustain",
      midi: note,
      velocity: 'velocity' in e ? e.velocity : undefined,
      tick,
      posMs,
      // L'instant visé et l'instant retenu, tous deux portés sur la grille.
      targetMs: posMs + (at - now) * 1000,
      playedMs: posMs + (played - now) * 1000,
      asap: eventTime === undefined,
    });
  }

  /**
   * Le nom du partenaire, quand la lane en porte un.
   *
   * Il vient de l'instrument, que `LiveControler` nomme d'après le
   * participant : c'est ce qui permet de lire un journal à trois sans confondre
   * deux mains.
   */
  private laneLabel(): string | null {
    const name = this.lane.getInstrument()?.name;

    return name !== undefined && name !== "" ? name : null;
  }

  /**
   * At play time, find notes whose noteOn is before the current tick but
   * whose noteOff is after, they are "sounding" and should be heard.
   * Trigger them immediately (at the current audio time).
   */
  private catchUpSoundingNotes(tick: number) {
    if (this.lane.isMuted()) return;

    const sounding = this.lane.getNotesInRange(tick, tick + 1);

    for (const n of sounding) {
      // Only catch up notes that started before the current tick
      if (n.tick >= tick) continue;

      const midi = n.note.toMidi();
      // Don't re-trigger if already pressed (scheduled or live)
      if (this.scheduledNotes.has(midi) || this.liveNotes.has(midi)) continue;

      this.scheduledNotes.set(midi, n);
      this.lane.synth.noteOn(midi, n.velocity);
    }
  }

  // ── Stop helpers ──

  /** Stop only scheduled (playback) notes, live notes are untouched. */
  private stopScheduledNotes() {
    this.releaseScheduledSustainedNotes();

    this.scheduledNotes.forEach((_n, midi) => {
      // Don't cut the synth note if a live note is holding the same MIDI key
      if (!this.liveNotes.has(midi)) {
        this.lane.synth.noteOff(midi);
      }
    });
    this.scheduledNotes.clear();
  }

  /**
   * Stop all notes (scheduled + live). Public API: used by callers outside
   * the transport lifecycle (e.g. Studio.stopAllScheduledNotes, exercise
   * teardown) to guarantee silence regardless of playback state.
   */
  stopAllNotes() {
    this.releaseScheduledSustainedNotes();
    this.releaseLiveSustainedNotes();

    const allMidis = new Set<number>();
    this.scheduledNotes.forEach((_n, midi) => allMidis.add(midi));
    this.liveNotes.forEach((_n, midi) => allMidis.add(midi));

    for (const midi of allMidis) {
      this.lane.synth.noteOff(midi);
    }

    this.scheduledNotes.clear();
    this.liveNotes.clear();
  }

  /**
   * Relâche ce que la pédale du morceau retenait.
   *
   * `at` est l'instant audio où elle se relève, que l'appelant connaît :
   * l'heure du `sustainOff` en lecture, rien du tout quand il s'agit d'obtenir
   * le silence — pause, saut, démontage. Le jumeau live dit la même chose pour
   * la même raison, voir `releaseLiveSustainedNotes`.
   */
  private releaseScheduledSustainedNotes(at?: number) {
    for (const midi of this.scheduledSustainedNotes) {
      if (!this.scheduledNotes.has(midi) && !this.liveNotes.has(midi)) {
        this.lane.synth.noteOff(midi, at);
      }
    }

    this.scheduledSustainedNotes.clear();
  }

  /**
   * Relâche ce que la pédale d'un partenaire retenait.
   *
   * `at` est l'instant audio où la pédale se relève. Absent — extinction
   * générale, démontage — on relâche maintenant, parce qu'il n'y a alors plus
   * de grille à respecter, seulement du silence à obtenir.
   *
   * Le passer compte depuis le délai de restitution : le `sustainOff` reçu
   * porte un tick encore devant notre tête de lecture, et relâcher « tout de
   * suite » raccourcirait chaque note tenue du délai. Auparavant les deux
   * revenaient au même — un geste distant sonnait toujours au plus tôt.
   */
  private releaseLiveSustainedNotes(at?: number) {
    for (const midi of this.liveSustainedNotes) {
      if (!this.liveNotes.has(midi) && !this.scheduledNotes.has(midi)) {
        this.lane.synth.noteOff(midi, at);
      }
    }

    this.liveSustainedNotes.clear();
  }

  destroy(): void {
    this.stopAllNotes();
    this.unsubscribers.forEach(unsub => unsub());
    this.unsubscribers.length = 0;
  }
}
